import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, Typography, Card, Space, message } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getUserProfile } from '../../redux/AuthController';
import { baseUrl } from '../../../utils/envAccess';

const { Title, Text } = Typography;

// Default pharmacy information (fallback if patient doesn't have pharmacy info)
const defaultPharmacy = {
  name: 'My Fertility Labs Pharmacy',
  address: '11444 17 Ave SW, Edmonton, AB, T6W 2S5',
  phone: '1-403-760-7017',
  fax: '1-825-902-8022'
};

const PharmacyConfirmationModal = ({ 
  visible, 
  onCancel, 
  onConfirm, 
  fileRef, 
  fileName,
  loading = false 
}) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [pharmacyData, setPharmacyData] = useState({
    name: '',
    address: '',
    phone: '',
    fax: ''
  });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const dispatch = useDispatch();
  const authState = useSelector((state) => state?.authentication?.userAuth);

  // Fetch patient pharmacy data
  const fetchPatientPharmacyData = useCallback(async () => {
    setLoadingProfile(true);
    try {
      // 1) Try Doctor/GetPatient/0/500 and match current patient.userRef
      const storedPatient = JSON.parse(localStorage.getItem('patient') || '{}');
      const currentPatientRef = storedPatient?.userRef || storedPatient?.id || storedPatient?.userId;
      const token = authState?.obj?.token || authState?.token || (JSON.parse(localStorage.getItem('userInfo') || '{}')?.token);

      if (token) {
        try {
          const listResp = await fetch(`${baseUrl}Doctor/GetPatient/0/500`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (listResp.ok) {
            const listData = await listResp.json();
            if (Array.isArray(listData)) {
              const matched = currentPatientRef
                ? listData.find((p) => (p?.userRef || p?.userId || p?.id) === currentPatientRef)
                : null;
              if (matched && (matched.pharmacyName || matched.pharmacyAddress || matched.pharmacyPhoneNumber || matched.pharmacyFaxNumber)) {
                const from500 = {
                  name: matched.pharmacyName || '',
                  address: matched.pharmacyAddress || '',
                  phone: matched.pharmacyPhoneNumber || '',
                  fax: matched.pharmacyFaxNumber || ''
                };
                setPharmacyData(from500);
                form.setFieldsValue(from500);
                setLoadingProfile(false);
                return;
              }
            }
          }
        } catch (e) {
          // Silent fallback to profile/localStorage/defaults
          console.warn('GetPatient/0/500 fetch failed, falling back:', e);
        }
      }

      const response = await dispatch(getUserProfile());
      const userData = response?.obj || response;
      
      if (userData && (userData.pharmacyName || userData.pharmacyAddress || userData.pharmacyPhoneNumber || userData.pharmacyFaxNumber)) {
        // Use patient's pharmacy data if available
        const patientPharmacy = {
          name: userData.pharmacyName || '',
          address: userData.pharmacyAddress || '',
          phone: userData.pharmacyPhoneNumber || '',
          fax: userData.pharmacyFaxNumber || ''
        };
        
        setPharmacyData(patientPharmacy);
        form.setFieldsValue(patientPharmacy);
        console.log('Loaded patient pharmacy data:', patientPharmacy);
      } else {
        // Fallback to saved pharmacy data or defaults
        const savedPharmacy = localStorage.getItem('pharmacyDetails');
        if (savedPharmacy) {
          try {
            const parsed = JSON.parse(savedPharmacy);
            setPharmacyData(parsed);
            form.setFieldsValue(parsed);
          } catch (error) {
            console.error('Error parsing saved pharmacy data:', error);
            setPharmacyData(defaultPharmacy);
            form.setFieldsValue(defaultPharmacy);
          }
        } else {
          setPharmacyData(defaultPharmacy);
          form.setFieldsValue(defaultPharmacy);
        }
      }
    } catch (error) {
      console.error('Error fetching patient pharmacy data:', error);
      // Fallback to defaults
      setPharmacyData(defaultPharmacy);
      form.setFieldsValue(defaultPharmacy);
    } finally {
      setLoadingProfile(false);
    }
  }, [dispatch, authState, form]);

  useEffect(() => {
    if (visible) {
      fetchPatientPharmacyData();
      setIsEditing(false);
    }
  }, [visible, fetchPatientPharmacyData]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    form.setFieldsValue(pharmacyData);
  };

  const handleSave = () => {
    form.validateFields()
      .then((values) => {
        setPharmacyData(values);
        setIsEditing(false);
        // Save to localStorage for future use
        localStorage.setItem('pharmacyDetails', JSON.stringify(values));
        message.success('Pharmacy details updated successfully');
      })
      .catch((errorInfo) => {
        console.error('Validation failed:', errorInfo);
      });
  };

  const handleConfirm = () => {
    form.validateFields()
      .then((values) => {
        // Save updated pharmacy data
        localStorage.setItem('pharmacyDetails', JSON.stringify(values));
        onConfirm(values, fileRef, fileName);
      })
      .catch((errorInfo) => {
        console.error('Validation failed:', errorInfo);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Title level={4} style={{ margin: 0 }}>
            Confirm Pharmacy Details
          </Title>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={handleConfirm}
          loading={loading}
          style={{ backgroundColor: '#00ADEF', borderColor: '#00ADEF' }}
        >
          Send Fax
        </Button>
      ]}
      destroyOnClose
    >
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          Please confirm the pharmacy details before sending the fax for: <strong>{fileName}</strong>
        </Text>
        {loadingProfile && (
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Loading patient pharmacy information...
            </Text>
          </div>
        )}
      </div>

      <Card 
        style={{ 
          border: '1px solid #d9d9d9',
          borderRadius: '8px',
          backgroundColor: '#fafafa'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <Title level={5} style={{ margin: 0 }}>
            Pharmacy Information
          </Title>
          {!isEditing ? (
            <Button 
              type="text" 
              icon={<EditOutlined />}
              onClick={handleEdit}
              style={{ color: '#00ADEF' }}
            >
              Edit
            </Button>
          ) : (
            <Space>
              <Button 
                type="text" 
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
                style={{ color: '#ff4d4f' }}
              >
                Cancel
              </Button>
              <Button 
                type="text" 
                icon={<CheckOutlined />}
                onClick={handleSave}
                style={{ color: '#52c41a' }}
              >
                Save
              </Button>
            </Space>
          )}
        </div>

        <Form
          form={form}
          layout="vertical"
          disabled={!isEditing || loadingProfile}
        >
          <Form.Item
            label="Pharmacy Name"
            name="name"
            rules={[{ required: true, message: 'Please enter pharmacy name' }]}
          >
            <Input 
              placeholder="Enter pharmacy name"
              style={{ 
                borderRadius: '6px',
                height: '40px'
              }}
            />
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: 'Please enter pharmacy address' }]}
          >
            <Input.TextArea 
              placeholder="Enter pharmacy address"
              rows={3}
              style={{ 
                borderRadius: '6px'
              }}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: 'Please enter phone number' },
                { pattern: /^[\d\s\-+()]+$/, message: 'Please enter a valid phone number' }
              ]}
              style={{ flex: 1 }}
            >
              <Input 
                placeholder="Enter phone number"
                style={{ 
                  borderRadius: '6px',
                  height: '40px'
                }}
              />
            </Form.Item>

            <Form.Item
              label="Fax Number"
              name="fax"
              rules={[
                { required: true, message: 'Please enter fax number' },
                { pattern: /^[\d\s\-+()]+$/, message: 'Please enter a valid fax number' }
              ]}
              style={{ flex: 1 }}
            >
              <Input 
                placeholder="Enter fax number"
                style={{ 
                  borderRadius: '6px',
                  height: '40px'
                }}
              />
            </Form.Item>
          </div>
        </Form>
      </Card>

      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        backgroundColor: '#e6f7ff', 
        borderRadius: '6px',
        border: '1px solid #91d5ff'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          <strong>Note:</strong> This information is loaded from the patient's profile. 
          You can edit these details anytime by clicking the "Edit" button above. 
          Changes will be saved for future fax transmissions.
        </Text>
      </div>
    </Modal>
  );
};

export default PharmacyConfirmationModal;
